import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Platform } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { modelsNotInModels } from '@app/functions';
import { Company } from '@app/models/company/company.model';
import { Employee } from '@app/models/employee/employee.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { INCOME_TYPES } from '@app/modules/time-tracking/meta/time-tracking-payroll-income-types';
import { TimeTrackingPayrollEmployeeIntegrationService } from '@app/modules/time-tracking/services/time-tracking-payroll-employee-integration.service';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AuthService, NotifyService } from '@app/services';
import { PayrollIncomeType, Settings } from '@time-tracking/interfaces/settings.interface';

@Component({
    templateUrl: './time-tracking-payroll-integration.template.html',
})
export class TimeTrackingPayrollIntegration implements OnInit {
    @ViewChild('confirmPayrollIntegrationModal') confirmPayrollIntegrationModal!: ConfirmationDialogComponent;

    isLoading = true;
    settings: Settings = {};
    incomeTypes: PayrollIncomeType[] = [];
    incomeTypesType = INCOME_TYPES;
    additionalIncomeTypes: CompanyAdditionalIncome[] = [];

    incomeTypesToDisplay: PayrollIncomeType[] = [
        { name: this.incomeTypesType.Regular, displayName: 'Regular Hours', id: null, enabled: true },
        { name: this.incomeTypesType.Overtime, displayName: 'Overtime', id: null, enabled: false },
    ];

    /**
     * employeesOnPayrollIntegration is the modifiable list of employees
     * When the user hits save, we use this list to save
     */
    employeesOnPayrollIntegration: Employee[] = [];

    /**
     * employeesOriginallyOnPayrollIntegration is the list of employees
     * that are actually on the integration at the DB level
     * we use this list to diff before saving.
     */
    employeesOriginallyOnPayrollIntegration: Employee[] = [];
    company: Company;

    constructor(
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private notifyService: NotifyService,
        private location: Location,
        private auth: AuthService,
        private timeTrackingPayrollEmployeeIntegrationService: TimeTrackingPayrollEmployeeIntegrationService
    ) {
        this.company = this.auth.company;
    }

    async ngOnInit(): Promise<void> {
        try {
            this.settings = await this.timeTrackingSettingsService.get();

            this.incomeTypes = this.settings.payrollIncomeTypes ?? [];

            this.loadEmployeesOnPayrollIntegration();
            await this.loadAdditionalIncomeTypes();

            this.combineIncomeTypes();
        } catch (err) {
            this.notifyService.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onCancel(): void {
        this.location.back();
    }

    onIncomeTypeChange(event: MatCheckboxChange): void {
        // This handles the case for regular pay and overtime since they do not have ids we match them on name
        if (!event.source.value) {
            this.incomeTypesToDisplay.forEach((incomeType) => {
                if (event.source.name === incomeType.name) {
                    incomeType.enabled = event.checked;
                }
            });

            return;
        }

        this.incomeTypesToDisplay.forEach((incomeType) => {
            if (+event.source.value === incomeType.id) {
                incomeType.enabled = event.checked;
            }
        });
    }

    onEmployeeIntegrationsChange(employees: Employee[]): void {
        this.employeesOnPayrollIntegration = [...employees];
    }

    async onPayrollIntegrationRadioChange(event: boolean): Promise<void> {
        this.settings.payrollIntegration = event;
    }

    async onSave(): Promise<void> {
        try {
            if (this.employeesPendingHours() && this.settings.payrollIntegration === false) {
                this.settings.payrollIntegration = true;

                await this.confirmPayrollIntegrationModal.open();

                return;
            }

            await this.timeTrackingSettingsService.set({
                payrollIntegration: this.settings.payrollIntegration,
                payrollIncomeTypes: this.incomeTypesToDisplay,
            });

            const employeesToAdd = modelsNotInModels(
                this.employeesOnPayrollIntegration,
                this.employeesOriginallyOnPayrollIntegration
            );
            const employeesToRemove = modelsNotInModels(
                this.employeesOriginallyOnPayrollIntegration,
                this.employeesOnPayrollIntegration
            );

            if (employeesToAdd.length + employeesToRemove.length !== 0) {
                await this.timeTrackingPayrollEmployeeIntegrationService.batch(employeesToAdd, employeesToRemove);
            }

            this.timeTrackingSettingsService.invalidate();
            this.location.back();
        } catch (err) {
            this.notifyService.error(err);
        }
    }

    private employeesPendingHours(): boolean {
        return this.employeesOnPayrollIntegration.some((employee) => employee.hasPendingTimeTrackingHours);
    }

    private combineIncomeTypes(): void {
        // If there are no income types stored then that means it's first time a company is using this.
        if (!this.incomeTypes.length) {
            this.incomeTypesToDisplay.push(...this.additionalIncomeTypes.map(this.convertAdditionalIncomeTypes));
            return;
        }

        // Filter additional income types from payroll if we already have them saved in the DB.
        // (i.e. get all names from array additionalIncomeTypes that are not in array incomeTypes)
        const filterAdditionalIncomeTypes = this.additionalIncomeTypes.filter((additionalIncomeType) => {
            return !this.incomeTypes.some((incomeType) => additionalIncomeType.name === incomeType.name);
        });

        this.incomeTypesToDisplay = [
            ...this.incomeTypes,
            ...filterAdditionalIncomeTypes.map(this.convertAdditionalIncomeTypes),
        ];
    }

    private convertAdditionalIncomeTypes = (additionalIncomeType: CompanyAdditionalIncome): PayrollIncomeType => {
        return {
            name: additionalIncomeType.name,
            displayName: additionalIncomeType.displayName,
            id: additionalIncomeType.id,
            enabled: false,
        };
    };

    private async loadAdditionalIncomeTypes(): Promise<void> {
        const [additionalIncomeTypes] = await CompanyAdditionalIncome.param('company', this.auth.company?.id)
            .where('active', true)
            .where('timeTracking', true)
            .get();

        this.additionalIncomeTypes = additionalIncomeTypes;
    }

    private async loadEmployeesOnPayrollIntegration(): Promise<void> {
        const [employees] = await Employee.param('company', this.company.id)
            .where('isAssignedModule', Platform.modules.timeTracking)
            .where('onTimeTrackingPayrollIntegration', true)
            .with(['department', 'job'])
            .with('timeTrackingAttributes')
            .all();

        this.employeesOnPayrollIntegration = [...employees];
        this.employeesOriginallyOnPayrollIntegration = [...employees];
    }
}
